import React from 'react';

import dayjs from 'dayjs';
import { Avatar, ModalProps, Toast, Modal } from '../../components';
import useSWR from 'swr';
import request from '../../request';
import Title from './Title';
import * as hosts from '../../constants/hosts';
import { useEvent } from '../../hooks';
import { AUTH } from '../../constants/hosts';
import { useHistory, useSelector, State } from '@medlinker/fundamental';
import qs from 'qs';
import { pushEmall } from '../../utils/urlHelper';
import history from '../../../app/history';
import { track } from '../../utils/EventTracker';
import numeral from 'numeral';

function Card(props: {
  src?: string;
  title: React.ReactNode;
  subTitle: React.ReactNode;
  price: number;
  originPrice?: number;
  btnText: string;
  freeTag?: boolean;
  /** 是否支付过 */
  isPay?: boolean;
  onBtnClick(): void;
}) {
  return (
    <div className="bg-[rgba(249,249,249,1)] rounded-[16px] py-[32px] px-[24px] h-[192px] flex relative">
      <Avatar
        className="w-[88px] h-[88px] rounded-full"
        size={200}
        src={props.src || ''}
      />
      <div className="mx-[20px] flex-1 overflow-hidden">
        <div className="text-[#2A2A2A] text-[34px] leading-[48px] font-medium">
          {props.title}
        </div>
        <div className="text-[#9a9a9a] text-[26px] mt-[4px] font-normal">
          {props.subTitle}
        </div>
        <div className="text-[#2A2A2A] text-[26px] mt-[20px]">
          问诊价格：
          {props.isPay ? (
            <span>无需支付</span>
          ) : (
            <>
              <span className="font-medium text-[#FF3030]">
                ￥{props.price}
              </span>
              {props.originPrice ? (
                <span className="font-medium text-[#a6a6a6] line-through ml-[4px]">
                  ￥{props.originPrice}
                </span>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div
        title="立即购药"
        className="w-[176px] h-[60px] mt-[34px] bg-[#1E8AFF] rounded-[30px] flex !items-center !justify-center text-[#fff] text-[28px]"
        onClick={props.onBtnClick}
      >
        {props.btnText}
      </div>

      {props.freeTag && (
        <img
          src={require('./free_tag.png')}
          className="absolute right-[12px] top-[42px] w-[105px] h-[34px]"
          onClick={props.onBtnClick}
        />
      )}
    </div>
  );
}

interface Props extends Omit<ModalProps, 'children'> {
  doctorId: number;
  transNo: number;
  /** 是否支付过问诊费用 */
  isPay: boolean;
  /** 医生问诊价格，单位：分 */
  payPrice: number;
}

const btn1Text = '立即购药';
const btn2Text = '立即购药';
const btn3Text = '付费问诊';

export default function SelectMethodModal(props: Props) {
  const { open, onClose, doctorId, transNo, isPay, payPrice } = props;
  const { createHref } = useHistory();
  const userInfo = useSelector((state: State) => state.user);
  const isCreating = React.useRef(false);

  const { data: doctorInfo } = useSWR(
    [`${hosts.D2D_PH}/patient/co-pa/doctor-detail`, { doctorId }],
    async ([url, params]) => {
      return await request.get<
        {},
        { avatar: string; name: string; inquiryPrice: number }
      >(url, { params });
    },
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
    }
  );

  /**
   * 1 随诊 2 开药门诊
   */
  const onClick = useEvent(async (type: 1 | 2) => {
    // 患者端复购点击选择医生
    userInfo?.info?.base?.userId &&
      track('fe_patient_repurchase_choose_doctor_btn_click', {
        patient_id: `${userInfo.info.base.userId}`,
        doctor_id: doctorId || '',
        goto_medicine_clinic: type === 2 ? true : false,
        repurchase_clinic_price: `${
          type === 2 ? 0 : numeral(payPrice).divide(100).value()!
        }`,
        click_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        click_result: '成功',
        click_btn_title: type === 2 ? btn1Text : isPay ? btn2Text : btn3Text,
      });

    if (!transNo) {
      Toast.info(
        '抱歉，您当前未有满足续方条件的处方，您可以在交流中请医生为您开具处方'
      );

      return;
    }

    history.push(`/order/create?reBuyPrescriptionId=${transNo}&flowTo=${type}`);
  });

  const createOrderTrack = ({
    success,
    order_id,
    order_trans_no,
    type,
  }: {
    success: boolean;
    order_id: string;
    order_trans_no: string;
    /** 2 随诊，1 开药门诊 */
    type: 1 | 2;
  }) => {
    if (!userInfo?.info?.base?.userId) return;

    // 患者复购问诊订单创建结果
    track('fe_created_repurchase_clinic_order_result', {
      patient_id: `${userInfo.info.base.userId}`,
      order_id,
      order_trans_no,
      order_created_result: success ? '成功' : '失败',
      order_created_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      amount: type === 2 ? payPrice : 5000,
      discount: type === 2 ? 0 : 5000,
      pay_money: type === 2 ? payPrice : 0,
    });
  };

  const handleCheckPay = useEvent(async () => {
    if (isCreating.current) return;
    if (isPay) {
      // 走随诊
      onClick(1);
    } else {
      let res: { transNo: string; orderId: number } | undefined;

      try {
        track('fe_patient_repurchase_choose_doctor_btn_click', {
          patient_id: `${userInfo.info.base.userId}`,
          doctor_id: doctorId || '',
          goto_medicine_clinic: false,
          repurchase_clinic_price: `${numeral(payPrice).divide(100).value()!}`,
          click_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          click_result: '成功',
          click_btn_title: btn3Text,
        });
      } catch (error) {
        // hack
      }

      try {
        isCreating.current = true;
        res = await request.post<any, { transNo: string; orderId: number }>(
          '/followup-interface/v1/chat_order/create',
          {
            baseURL: hosts.API,
            data: {
              doctorId,
              bagType: 5,
              chatType: 2,
              channel: '',
              num: 1,
              isReConsultation: true,
            },
          }
        );
        createOrderTrack({
          success: true,
          order_id: res.orderId + '',
          order_trans_no: res.transNo || '',
          type: 2,
        });
      } catch (error) {
        createOrderTrack({
          success: false,
          order_id: '',
          order_trans_no: '',
          type: 2,
        });
        Toast.fail(error.message);
      } finally {
        isCreating.current = false;
      }
      if (!res) return;

      const redirectUrl = encodeURIComponent(
        `${AUTH}${createHref({
          pathname: '/order/create',
          search: qs.stringify(
            {
              reBuyPrescriptionId: transNo,
              // 1:随诊，2：开药门诊
              flowTo: 1,
            },
            { addQueryPrefix: true }
          ),
        })}`
      );

      pushEmall({
        path: '/payment',
        query: {
          orderNum: res.transNo,
          orderType: 3,
          categoryTitle: '订单支付',
          redirect: redirectUrl,
        },
      });
    }
  });

  return (
    <Modal open={open} anchor="bottom" onClose={onClose}>
      <div className="">
        <Title title="选择购药方式" onClose={onClose} />

        <div className="max-h-[949px] overflow-y-auto bg-[#fff]">
          <div className="px-[32px] pt-[30px] pb-[60px] space-y-[30px]">
            <Card
              src={require('./doctor.png')}
              title="在线医生"
              subTitle="快速购药仅需10秒"
              price={0}
              btnText={btn1Text}
              originPrice={50}
              freeTag
              onBtnClick={() => {
                onClick(2);
              }}
            />
            <Card
              src={doctorInfo?.avatar || require('./doctor2.png')}
              title={doctorInfo?.name || '-'}
              subTitle="购买问诊包提交购药申请"
              price={numeral(payPrice).divide(100).value()!}
              btnText={isPay ? btn2Text : btn3Text}
              onBtnClick={handleCheckPay}
              isPay={isPay}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
